import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Info } from 'lucide-react';
import { PricingModal } from './PricingModal';
const barbers = [{
  id: 1,
  name: 'Nicolas Marshall',
  specialty: 'Colorimetría & Cortes de Autor',
  image: 'https://images.unsplash.com/photo-1618085220188-b4f210d22703?auto=format&fit=crop&q=80&w=400'
}, {
  id: 2,
  name: 'Mateo Silva',
  specialty: 'Fades & Diseños Urbanos',
  image: 'https://images.unsplash.com/photo-1593702275687-f8b402bf1fb5?auto=format&fit=crop&q=80&w=400'
}, {
  id: 3,
  name: 'Lucas Pereira',
  specialty: 'Barba & Estilo Clásico',
  image: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&q=80&w=400'
}];
export function BookingSystem() {
  const [step, setStep] = useState(1);
  const [selectedBarber, setSelectedBarber] = useState<number | null>(null);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const scrollToSection = () => {
    if (sectionRef.current) {
      const yOffset = -80; // Adjust for navbar height
      const y = sectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const handleBarberSelect = (id: number) => {
    setSelectedBarber(id);
    setStep(2);
    scrollToSection();
  };
  // Load Calendly script when step 2 is active
  useEffect(() => {
    if (step === 2) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [step]);
  return <section id="reservar" ref={sectionRef} className="py-32 bg-[#0a0a0a] border-t border-white/5 relative overflow-hidden" data-id="element-62">
      {/* Subtle gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-[#c9a84c]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-63" />

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10" data-id="element-64">
        <div className="text-center mb-12" data-id="element-65">
          <h2 className="font-display text-5xl md:text-6xl mb-4 text-white" data-id="element-66">
            Reserva tu <span className="text-[#c9a84c]" data-id="element-67">Turno</span>
          </h2>
          <p className="text-gray-400 text-lg mb-6" data-id="element-68">
            Selecciona tu barbero, elige el horario y asegura tu lugar.
          </p>
          <button onClick={() => setIsPricingModalOpen(true)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#c9a84c]/50 text-gray-300 hover:text-white transition-all text-sm font-medium backdrop-blur-sm" data-id="element-69">
            <Info size={16} className="text-[#c9a84c]" data-id="element-70" />
            Ver lista de precios
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12 max-w-[200px] mx-auto" data-id="element-71">
          {[1, 2].map(i => <Fragment key={i} data-id="element-72">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300 ${step >= i ? 'bg-[#c9a84c] text-black' : 'bg-white/5 text-gray-500 border border-white/10'}`} data-id="element-73">
                {i}
              </div>
              {i < 2 && <div className={`flex-1 h-[2px] transition-colors duration-300 ${step > i ? 'bg-[#c9a84c]' : 'bg-white/10'}`} data-id="element-74" />}
            </Fragment>)}
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-3xl p-6 md:p-10 backdrop-blur-xl min-h-[500px] relative" data-id="element-75">
          <AnimatePresence mode="wait" data-id="element-76">
            {/* STEP 1: Select Barber */}
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-77">
                <h3 className="text-2xl font-semibold text-white mb-8 text-center" data-id="element-78">
                  Selecciona un Profesional
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-79">
                  {barbers.map(barber => <div key={barber.id} className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden hover:border-[#c9a84c]/50 transition-all duration-300 group" data-id="element-80">
                      <div className="aspect-square overflow-hidden" data-id="element-81">
                        <img src={barber.image} alt={barber.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" data-id="element-82" />
                      </div>
                      <div className="p-6 text-center" data-id="element-83">
                        <h4 className="text-xl font-bold text-white mb-1" data-id="element-84">
                          {barber.name}
                        </h4>
                        <p className="text-sm text-[#c9a84c] mb-6 h-10" data-id="element-85">
                          {barber.specialty}
                        </p>
                        <button onClick={() => handleBarberSelect(barber.id)} className="w-full py-3 rounded-xl bg-white/5 hover:bg-[#c9a84c] hover:text-black text-white font-semibold transition-colors duration-300 border border-white/10 hover:border-[#c9a84c]" data-id="element-86">
                          Seleccionar
                        </button>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {/* STEP 2: Calendly Embed */}
            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="flex flex-col h-full" data-id="element-87">
                <div className="flex items-center mb-4" data-id="element-88">
                  <button onClick={() => setStep(1)} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-id="element-89">
                    <ChevronLeft size={20} data-id="element-90" />
                  </button>
                  <h3 className="text-2xl font-semibold text-white flex-1 text-center pr-10" data-id="element-91">
                    Agenda tu Cita
                  </h3>
                </div>

                <div className="w-full bg-transparent rounded-2xl overflow-hidden" data-id="element-92">
                  <iframe src="https://calendly.com/barberia-ninja-uy/reserva-nicolas?hide_event_type_details=1&background_color=0a0a0a&text_color=ffffff&primary_color=eab308" width="100%" height="700px" frameBorder="0" title="Calendly Scheduling Page" data-id="element-93"></iframe>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <PricingModal isOpen={isPricingModalOpen} onClose={() => setIsPricingModalOpen(false)} data-id="element-94" />
    </section>;
}